import React, { useState, useEffect, useRef, useMemo, useReducer } from "react";
import { createPortal } from "react-dom";
import MagnifyingGlassIcon from "../../Icons/magnifying-glass-icon";
import styles from "./AdvancedMultiCompanySelectorInput.module.css";
import {
  NODE_TYPE,
  useEnterpriseStructure,
} from "../../../Hooks/useEnterpriseStructure";
import { cx } from "../../../Util/classHelpers";

const ChevronDown = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M2 6L5 9L10 3"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IndeterminateIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M2 6H10" stroke="white" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const initialState = new Map();

// Action types
const RESET = "RESET";
const EXPANSION_TOGGLE = "EXPANSION_TOGGLE";
const SELECTION_TOGGLE = "SELECTION_TOGGLE";
const CLEAR_SELECTION = "CLEAR_SELECTION";
const SET_SEARCH_QUERY = "SET_SEARCH_QUERY";

function populateStateFromTree(tree, state = new Map(), selectedCompanyIds) {
  if (!tree) return state;

  const key = `${tree.type}_${tree.id}`;

  if (tree.children && Array.isArray(tree.children)) {
    tree.children.forEach((child) => {
      populateStateFromTree(child, state, selectedCompanyIds);
    });
  }

  if (tree.type === NODE_TYPE.COMPANY) {
    state.set(key, {
      visible: true,
      selected: selectedCompanyIds.includes(tree.id) ? "checked" : "empty",
      expanded: null,
      count: 1,
    });
  } else {
    const childCount = tree.children.length;
    const selectedCount = tree.children.filter((child) => {
      const childState = state.get(`${child.type}_${child.id}`);
      return childState && childState.selected === "checked";
    }).length;
    const emptyCount = tree.children.filter((child) => {
      const childState = state.get(`${child.type}_${child.id}`);
      return childState && childState.selected === "empty";
    }).length;

    state.set(key, {
      visible: true,
      selected:
        emptyCount > 0 && emptyCount === childCount
          ? "empty"
          : selectedCount === childCount
            ? "checked"
            : "indeterminate",
      expanded: true,
      count: tree.children.reduce((acc, child) => {
        const childState = state.get(`${child.type}_${child.id}`);
        return acc + (childState?.count || 0);
      }, 0),
    });
  }
  return state;
}

function setVisible(tree, query, state, newState) {
  const key = `${tree.type}_${tree.id}`;

  if (newState.has(key)) {
    return newState.get(key);
  }

  const currentState = state.get(key);

  const newValue = { ...currentState };
  newValue.visible = false;
  newValue.count = 0;
  newState.set(key, newValue);

  if (tree.type === NODE_TYPE.COMPANY) {
    if (tree.name && tree.name.toLowerCase().includes(query.toLowerCase())) {
      newValue.visible = true;
      newValue.count = 1;
    }
  } else {
    for (let child of tree.children) {
      const childValue = setVisible(child, query, state, newState);
      newValue.visible = newValue.visible || childValue.visible;
      newValue.count += childValue.count;
    }
  }

  return newValue;
}

function toggleCompanies(node, state, newState, selected) {
  const key = `${node.type}_${node.id}`;

  if (node.type === NODE_TYPE.COMPANY) {
    const newValue = { ...state.get(key) };
    newValue.selected = selected;
    newState.set(key, newValue);
  } else {
    for (let child of node.children) {
      toggleCompanies(child, state, newState, selected);
    }
  }
}

function fillSelecteds(node, state, newState) {
  const key = `${node.type}_${node.id}`;
  if (newState.has(key)) {
    return;
  }

  for (let child of node.children) {
    fillSelecteds(child, state, newState);
  }

  if (node.type === NODE_TYPE.COMPANY) {
    const newValue = { ...state.get(key) };
    newState.set(key, newValue);
  } else {
    const childCount = node.children.length;
    const selectedCount = node.children.filter((child) => {
      const childState = newState.get(`${child.type}_${child.id}`);
      return childState && childState.selected === "checked";
    }).length;
    const emptyCount = node.children.filter((child) => {
      const childState = newState.get(`${child.type}_${child.id}`);
      return childState && childState.selected === "empty";
    }).length;

    const newValue = { ...state.get(key) };
    newValue.selected =
      emptyCount > 0 && emptyCount === childCount
        ? "empty"
        : selectedCount === childCount
          ? "checked"
          : "indeterminate";
    newState.set(key, newValue);
  }
  return;
}

export function reducer(state, action) {
  switch (action.type) {
    case RESET: {
      const newState = new Map();
      populateStateFromTree(action.tree, newState, action.selectedCompanyIds);
      return newState;
    }
    case EXPANSION_TOGGLE: {
      const newState = new Map(state);
      const key = `${action.node.type}_${action.node.id}`;
      const newValue = { ...newState.get(key) };
      newValue.expanded = !newValue.expanded;
      newState.set(key, newValue);
      return newState;
    }
    case SELECTION_TOGGLE: {
      console.log("TOGGLE");
      const newState = new Map();
      const key = `${action.node.type}_${action.node.id}`;
      // console.log(state);
      toggleCompanies(
        action.node,
        state,
        newState,
        state.get(key).selected === "empty" ? "checked" : "empty",
      );
      // console.log(newState);
      fillSelecteds(action.tree, state, newState);
      return newState;
    }
    case CLEAR_SELECTION: {
      const newState = new Map();
      state.forEach((value, key) => {
        const newValue = { ...value };
        newValue.selected = "empty";
        newState.set(key, newValue);
      });
      return newState;
    }
    case SET_SEARCH_QUERY: {
      if (!action.tree) return state;
      const newState = new Map();
      setVisible(action.tree, action.searchQuery, state, newState);
      return newState;
    }
    default:
      return state;
  }
}

const Checkbox = ({ state }) => {
  return (
    <span
      className={cx(styles.checkbox,
        state === "checked" && styles.checkboxChecked,
       state === "indeterminate" && styles.checkboxIndeterminate)}
      role="presentation"
    >
      {state === "checked" && <CheckIcon />}
      {state === "indeterminate" && <IndeterminateIcon />}
    </span>
  );
};

const Tree = ({ node, state, dispatch, tree }) => {
  const key = `${node.type}_${node.id}`;
  const nodeState = state.get(key);

  if (!nodeState) {
    return null;
  }

  const hasChildren = node.children.length > 0;
  const visibleChildren = hasChildren
    ? node.children.filter((child) => {
        const childState = state.get(`${child.type}_${child.id}`);
        return childState && childState.visible;
      })
    : [];
  const isExpanded = hasChildren && nodeState.expanded;

  return (
    <div className={styles.treeNodeWrapper}>
      <div className={styles.treeRow} role="button" onClick={(e) => {
          e.stopPropagation();
          dispatch({ type: SELECTION_TOGGLE, node: node, tree: tree });
        }}>
        <Checkbox state={nodeState.selected} />
        <span className={styles.rowLabel}>{node.name}</span>
        {node.type !== NODE_TYPE.COMPANY && <span>{nodeState.count}</span>}
        {node.type !== NODE_TYPE.COMPANY && <span
            className={cx(styles.rowChevron, nodeState.expanded && styles.rowChevronExpanded)}
            onClick={(e) => {
              e.stopPropagation();
              dispatch({ type: EXPANSION_TOGGLE, node: node });
            }}
            role="button"
            tabIndex={0}
            aria-label={nodeState.expanded ? "Collapse" : "Expand"}
            aria-hidden="true"
          >
            <ChevronDown />
          </span>}
      </div>
      {isExpanded && (
        <div className={styles.treeChildren}>
          <div className={styles.treeChildrenLineColumn}>
            <div className={styles.treeChildrenLine} />
          </div>
          <div className={styles.treeChildrenContent}>
            {visibleChildren.map((child) => (
              <Tree
                key={child.id}
                node={child}
                state={state}
                dispatch={dispatch}
                tree={tree}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function AdvancedMultiCompanySelectorInput({
  companyIds = [],
  setCompanyIds,
  additionalClasses,
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [state, dispatch] = useReducer(reducer, initialState);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(undefined);
  const [dropdownPosition, setDropdownPosition] = useState(null);

  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gapFromBottom = 24;
    const available = window.innerHeight - rect.bottom - 4 - gapFromBottom;
    setDropdownMaxHeight(Math.max(120, available));
    setDropdownPosition({
      top: rect.bottom + 4,
      left: rect.left,
      minWidth: rect.width,
    });
  }, [open]);

  const { tree, companies } = useEnterpriseStructure();

  useEffect(() => {
    if (open) {
      dispatch({ type: RESET, tree: tree, selectedCompanyIds: companyIds });
      setSearchQuery("");
    }
  }, [open, tree]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const insideTrigger = wrapperRef.current?.contains(e.target);
      const insideDropdown = dropdownRef.current?.contains(e.target);
      if (!insideTrigger && !insideDropdown) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleTrigger = () => setOpen((o) => !o);

  useEffect(() => {
    dispatch({ type: SET_SEARCH_QUERY, searchQuery: searchQuery, tree: tree });
  }, [searchQuery, tree]);

  const displayLabel = useMemo(() => {
    if (companyIds.length === 0) return "All locations";
    if (companyIds.length === companies.length) return "All locations";
    return `${companyIds.length} location${
      companyIds.length === 1 ? "" : "s"
    } selected`;
  }, [companyIds, companies]);

  const handleApply = () => {
    const selectedCompanyIds = Array.from(state.entries())
      .filter(([key, value]) => value.selected === "checked")
      .map(([key, value]) => key.split("_"))
      .filter(([type, id]) => type === NODE_TYPE.COMPANY)
      .map(([type, id]) => parseInt(id));
    setCompanyIds(selectedCompanyIds);
    setOpen(false);
  };

  return (
    <div className={cx(styles.wrapper, additionalClasses)} ref={wrapperRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onClick={handleTrigger}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Select locations"
        data-options-open={open}
      >
        <span className={styles.triggerLabel}>{displayLabel}</span>
        <span className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}>
          <ChevronDown />
        </span>
      </button>
      {open &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            className={styles.dropdown}
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: 500,
              maxHeight: dropdownMaxHeight ?? 360,
            }}
          >
            <div className={styles.searchWrap}>
              <div className={styles.searchBar}>
                <span className={styles.searchIconWrap} aria-hidden="true">
                  <MagnifyingGlassIcon />
                </span>
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search locations"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  aria-label="Search locations"
                />
              </div>
            </div>
            <div className={styles.listWrap}>
              <Tree node={tree} state={state} dispatch={dispatch} tree={tree} />
            </div>
            <div className={styles.actions}>
              <div
                role="button"
                tabIndex={0}
                className={styles.btnClear}
                onClick={() => dispatch({ type: CLEAR_SELECTION })}
              >
                Clear
              </div>
              <div
                role="button"
                tabIndex={0}
                className={styles.btnApply}
                onClick={handleApply}
              >
                Apply
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
